<script lang="ts">
	/**
	 * The visit list (§12.10) — the panel that makes the rest of the page answerable.
	 *
	 * Every figure above this is an aggregate, and an aggregate can only ever tell you *that*
	 * something happened. This is where somebody clicks through to one real journey and finds out
	 * why — which is also the question support arrives with: "they say they tried to buy twice and
	 * it did not work", and until this existed the honest answer was that nothing was written down.
	 */

	import {
		Button,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import { EmptyState, Panel, RelativeDate } from '../../../components';
	import type { AnalyticsQuery, AnalyticsSessionRow } from '../../../AppTypes';
	import { listAnalyticsSessions } from '../../../AppNetwork';
	import { analyticsStageName, analyticsVisitDuration } from '../../../Utilities';

	export let filter: AnalyticsQuery;

	const dispatcher = createEventDispatcher<{ open: string }>();
	const PAGE = 25;

	let rows: AnalyticsSessionRow[] = [];
	let total = 0;
	let offset = 0;
	let error = '';
	let loading = false;


	$: queryKey = JSON.stringify(filter);
	$: void fnReset(queryKey);

	async function fnReset(_key: string): Promise<void> {
		offset = 0;
		rows = [];
		await fnLoad();
	}

	async function fnLoad(): Promise<void> {
		loading = true;
		try {
			const page = await listAnalyticsSessions(filter, { limit: PAGE, offset });
			// Replace on the first page, append on the rest: a filter change must not leave the
			// previous filter's visits at the top of the list.
			rows = offset === 0 ? page.items : [...rows, ...page.items];
			total = page.total;
			error = '';
		} catch (caught) {
			error = caught instanceof Error ? caught.message : 'Failed to load visits';
		} finally {
			loading = false;
		}
	}

	async function fnMore(): Promise<void> {
		offset += PAGE;
		await fnLoad();
	}
</script>

<Panel title="Visits" faIcon="list-ul">
	<svelte:fragment slot="titleRight">
		<span class="text-xs text-default">{total.toLocaleString()} in this range</span>
	</svelte:fragment>

	{#if error.length > 0}
		<EmptyState variant="error" title="Visits could not be loaded." description={error} />
	{:else if rows.length === 0 && loading}
		<EmptyState variant="loading" />
	{:else if rows.length === 0}
		<EmptyState variant="empty" title="No visits match this filter." compact />
	{:else}
		<div class="overflow-x-auto">
			<Table class="dataTable">
				<TableHead>
					<TableHeadCell>Started</TableHeadCell>
					<TableHeadCell>Who</TableHeadCell>
					<TableHeadCell>Got as far as</TableHeadCell>
					<TableHeadCell>From</TableHeadCell>
					<TableHeadCell>Landed on</TableHeadCell>
					<TableHeadCell class="text-right">Events</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each rows as row (row.sessionUUID)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">
								<button
									type="button"
									class="text-primary-600 dark:text-primary-400 hover:underline"
									on:click={() => dispatcher('open', row.sessionUUID)}
								>
									<RelativeDate date={row.startedAt} />
								</button>
								<!--
									Whether it is still going is the server's answer, from the store's own
									idle window — a copy of that setting here would drift the moment an
									operator changed it, and this list would call a visit finished while
									the abandonment report still counted it in progress.
								-->
								<span class="text-default text-xs block">
									{#if row.inProgress}
										still going
									{:else}
										lasted {analyticsVisitDuration(row.durationSeconds)}
									{/if}
									{#if row.isBot}· Bot{/if}
								</span>
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{row.userEmail ?? 'Not signed in'}
								<span class="text-default text-xs block">
									{row.country ?? '—'} · {row.deviceType ?? 'unknown'} · {row.browser ?? 'unknown'}
								</span>
							</TableBodyCell>
							<TableBodyCell class="dataTable">{analyticsStageName(row.maxStage)}</TableBodyCell>
							<TableBodyCell class="dataTable">
								{row.utmSource ?? row.referrerHost ?? 'Direct'}
							</TableBodyCell>
							<TableBodyCell class="dataTable">{row.entryPath ?? '—'}</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">{row.eventCount}</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		</div>

		{#if rows.length < total}
			<div class="flex justify-center mt-3">
				<Button size="xs" color="alternative" disabled={loading} on:click={fnMore}>
					{loading ? 'Loading…' : `Load ${Math.min(PAGE, total - rows.length)} more`}
				</Button>
			</div>
		{/if}
	{/if}
</Panel>
